import React, {useEffect, useState} from "react";
import _ from "lodash";
import {Button, Paper, IconButton} from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";

import WorkflowSelector from "./workflow_selector";
import getCopiedWorkflowInputValues from "../utils/get_copied_workflow_input_values";
import * as InputEditor from "common_components/flowmaster/input_editors";

import {
  Workflow,
  WorkflowContext,
  WorkflowId,
  WorkflowInputs as WorkflowInputsType,
} from "common/flowmaster/types/workflow";
import {Issue} from "common/types/issue";
import {OrganisationId} from "common/types/organisation";
import {OverrideableFieldData} from "./workflow_editor";

interface WorkflowInputsProps {
  inputs: WorkflowInputsType;
  selectedWorkflow: Workflow;
  selectedWorkflowId: WorkflowId;
  updateWorkflowSettings: (updatedSettings: WorkflowInputsType) => void;
  organisationId: OrganisationId;
  workflows: Workflow[];
  workflowSettings: Issue["llm_settings"]["workflow_settings"];
  isOverrideMode: boolean;
  isDisplayedInPlaybook: boolean;
  getOverridableFieldData: (
    fieldType: string,
    key: string,
    fieldMethods: Record<string, unknown>,
    emptyValue: unknown,
    useProposedChanges: boolean,
  ) => OverrideableFieldData;
  renderOverridenIssuesets?: (
    fieldName: string,
    props?: unknown,
    handleHoverValue?: unknown,
  ) => React.ReactNode;
  contextTypeId: WorkflowContext["id"];
}

const styles: Record<string, React.CSSProperties> = {
  paper: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  cardHeader: {
    paddingLeft: "2rem",
    fontSize: "17px",
    fontWeight: 500,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  placeholder: {
    padding: "2rem",
    background: "rgba(0, 0, 0, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
  },
  overrideContainer: {
    marginLeft: 0,
    marginRight: 0,
    marginTop: -10,
  },
};

const WorkflowInputs = ({
  inputs,
  selectedWorkflow,
  selectedWorkflowId,
  updateWorkflowSettings,
  organisationId,
  workflows,
  workflowSettings,
  isOverrideMode,
  getOverridableFieldData,
  contextTypeId,
  isDisplayedInPlaybook,
  renderOverridenIssuesets,
}: WorkflowInputsProps) => {
  const [workflowToCopy, setWorkflowToCopy] = useState<WorkflowId>(() => {
    const savedWorkflowToCopy = sessionStorage.getItem("workflowToCopy");
    return savedWorkflowToCopy ? Number(savedWorkflowToCopy) : -1;
  });
  const [tempInputs, setTempInputs] = useState(inputs);

  useEffect(() => {
    // sync new values when switching issues
    setTempInputs(inputs);
  }, [inputs]);

  const handleInputChange = (name, newValue) => {
    const newTempInputs = {...tempInputs, [name]: newValue};
    setTempInputs(newTempInputs);
  };

  const handleInputBlur = () => {
    if (!_.isEqual(inputs, tempInputs)) {
      updateWorkflowSettings(tempInputs);
    }
  };

  const handleSelectCopyWorkflowInputs = workflowId => {
    sessionStorage.setItem("workflowToCopy", workflowId.toString());
    setWorkflowToCopy(workflowId);
  };

  const handleCopyInputs = () => {
    if (workflowToCopy === -1) {
      return;
    }

    const inputsToCopy =
      workflowSettings?.config?.[workflowToCopy]?.inputs.values ?? {};

    const updates = getCopiedWorkflowInputValues(
      workflows,
      workflowToCopy,
      inputs,
      inputsToCopy,
      selectedWorkflow,
    );

    if (!_.isEqual(inputs, updates)) {
      updateWorkflowSettings(updates);
    }
  };

  const currentWorkflowInputNames = selectedWorkflow?.inputs?.values?.map(
    input => input.name,
  );

  const copyInputWorkflowOptions = workflows.filter(
    wf =>
      wf.id !== selectedWorkflowId &&
      wf.inputs?.values?.some(input =>
        currentWorkflowInputNames?.includes(input.name),
      ),
  );

  const renderOverrideInput = input => {
    const Component = getInputComponent(input);
    const fieldName = `inputs.input_${input.id}`;

    const isModelSelector = input.type === "llm_model";
    const shouldDisable = isOverrideMode && input.is_overrideable === false;

    const fieldData = getOverridableFieldData(
      isModelSelector ? "SelectFieldV4" : "TextField",
      `inputs.input_${input.id}`,
      {
        setValueTo: (fieldName, fields, value) => {
          const [parent, key] = fieldName.split(".");
          if (!fields[parent]) {
            fields[parent] = {};
          }
          fields[parent][key] = value;
        },
        deleteValueFrom: (fieldName, fields) => {
          const [parent, key] = fieldName.split(".");
          if (fields[parent]) {
            delete fields[parent][key];
          }
        },
      },
      undefined,
      false,
    );

    return (
      <div
        key={input.id}
        style={{
          display: "flex",
          flexDirection: "column",
          marginBottom: "16px",
        }}
        title={shouldDisable ? "This input cannot be overridden" : ""}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Component
            style={{marginBottom: "1em"}}
            input={input}
            fullWidth={!isModelSelector}
            handleInputChange={fieldData.props.onChange}
            handleInputBlur={handleInputBlur}
            isInIssueEditor={true}
            {..._.omit(fieldData.props, ["textareaStyle", "inputStyle"])}
          />
          <IconButton
            {...fieldData.clearProps}
            style={{marginTop: isModelSelector ? 0 : 8}}
          >
            <ClearIcon />
          </IconButton>
        </div>
        {renderOverridenIssuesets &&
          renderOverridenIssuesets(fieldName, {
            containerStyle: styles.overrideContainer,
          })}
      </div>
    );
  };

  const renderBaseInput = input => {
    const Component = getInputComponent(input);
    const fieldName = `inputs.input_${input.id}`;

    const handleChange = (event: React.ChangeEvent<{value: string}>) => {
      const {value} = event.target;
      if (input.type === "llm_model") {
        updateWorkflowSettings({...tempInputs, [input.id]: value});
      } else if (input.type === "schema") {
        handleInputChange(input.id, JSON.stringify(value));
      } else {
        handleInputChange(input.id, value);
      }
    };

    const value =
      input.type === "schema"
        ? JSON.parse(tempInputs[input.id] || "{}")
        : tempInputs[input.id] || "";

    return (
      <div
        key={input.id}
        style={{
          display: "flex",
          flexDirection: "column",
          marginBottom: "16px",
        }}
      >
        <Component
          key={input.id}
          input={input}
          value={value}
          fullWidth={true}
          handleInputChange={handleChange}
          handleInputBlur={handleInputBlur}
          isInIssueEditor={true}
          style={{marginBottom: "1em"}}
        />
        {renderOverridenIssuesets &&
          renderOverridenIssuesets(fieldName, {
            containerStyle: styles.overrideContainer,
          })}
      </div>
    );
  };

  if (
    isDisplayedInPlaybook &&
    selectedWorkflow &&
    (!selectedWorkflow?.inputs?.values ||
      selectedWorkflow.inputs.values.length === 0)
  ) {
    return (
      <Paper style={styles.paper}>
        <div style={{...styles.placeholder, background: "inherit"}}>
          No prompts available to configure for this issue
        </div>
      </Paper>
    );
  }

  return (
    <Paper style={styles.paper}>
      {!isDisplayedInPlaybook && (
        <div className="app-toolbar" style={styles.cardHeader}>
          <div>Workflow Inputs</div>
          {!isOverrideMode &&
            copyInputWorkflowOptions.length > 0 &&
            selectedWorkflow?.inputs.values.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <WorkflowSelector
                  workflows={copyInputWorkflowOptions}
                  selectedWorkflowId={workflowToCopy}
                  organisationId={organisationId}
                  updateselectedWorkflowId={handleSelectCopyWorkflowInputs}
                  contextTypeIds={[contextTypeId]}
                  label="Copy values from"
                  showStarredWorkflows
                />

                <Button
                  style={{marginLeft: "1rem"}}
                  variant="contained"
                  onClick={handleCopyInputs}
                >
                  Copy
                </Button>
              </div>
            )}
        </div>
      )}
      <div style={{padding: "2rem", display: "flex", flexDirection: "column"}}>
        {selectedWorkflow?.inputs.values.map(input =>
          isOverrideMode ? renderOverrideInput(input) : renderBaseInput(input),
        )}
      </div>
    </Paper>
  );
};

function getInputComponent(input) {
  return input.type
    ? InputEditor[input.type].component
    : InputEditor["string"].component;
}

export default WorkflowInputs;
